/**
 * Accept Invite Page
 * Allows accountants to accept report collaboration invites via token
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const { accountant, getAuthHeader } = useAccountantAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteDetails, setInviteDetails] = useState<any>(null);

  useEffect(() => {
    if (!accountant) {
      // Store the invite token and redirect to login
      sessionStorage.setItem('pendingInviteToken', token || '');
      navigate(`/accountant/login?returnUrl=${encodeURIComponent(`/accountant/accept-invite/${token}`)}`);
      return;
    }

    // Accountant is logged in, accept the invite
    acceptInvite();
  }, [accountant, token]);

  const acceptInvite = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/invites/accept`,
        { invite_token: token },
        { headers: getAuthHeader() }
      );

      setInviteDetails(response.data);
      setSuccess(true);
      
      // Auto-redirect to the report after 2 seconds
      setTimeout(() => {
        navigate(`/accountant/reports/${response.data.report_id}`);
      }, 2000);
      
    } catch (err: any) {
      console.error('Error accepting invite:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to accept invite. The link may be invalid or expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Processing Invite...
          </h2>
          <p className="text-gray-600">
            Please wait while we activate your access
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Unable to Accept Invite
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
          </div>

          <div className="space-y-3">
            <Link
              to="/accountant/dashboard"
              className="block w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-center font-medium"
            >
              Go to Dashboard
            </Link>
            <Link
              to="/accountant/login"
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-center font-medium"
            >
              Login with Different Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success && inviteDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ✨ Invite Accepted!
            </h2>
            <p className="text-gray-600 mb-4">
              You now have access to this report
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
              <p className="text-sm text-gray-600 mb-1">Report Shared By:</p>
              <p className="font-semibold text-gray-900 mb-3">{inviteDetails.shared_by_name}</p>
              
              <p className="text-sm text-gray-600 mb-1">Your Permissions:</p>
              <div className="flex flex-wrap gap-2">
                {inviteDetails.permissions.map((perm: string) => (
                  <span 
                    key={perm}
                    className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                  >
                    {perm}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <p className="text-sm text-center text-gray-500 mb-4">
            Redirecting you to the report...
          </p>

          <Link
            to={`/accountant/reports/${inviteDetails.report_id}`}
            className="block w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-center font-medium"
          >
            View Report Now
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
